import { type FC, type PropsWithChildren, useMemo } from 'react';
import castArray from 'lodash/castArray';
import { Popover, Typography } from '@timeedit/ui-components/antd';
import { getLocalDisplayFormatFromTimestamp } from '../../utils/timeConverter';

type HistoryRecord = {
  time?: number;
  label?: string;
};

type Props = {
  label: string;
  history?: HistoryRecord | HistoryRecord[];
};

const HistoryDetail: FC<Props> = (props) => {
  const latestHistory = Array.isArray(props.history)
    ? props.history[0]
    : props.history;

  const history = useMemo(
    () => castArray(props.history ?? []).slice(1, 10),
    [props.history]
  );

  const latestTime = useMemo(
    () => renderTime(latestHistory?.time),
    [latestHistory]
  );

  return (
    <Typography.Text type="secondary">
      <HistoryPopover history={history}>
        {props.label}: {latestTime}
        {latestHistory?.label ? ` (${latestHistory.label})` : ''}
      </HistoryPopover>
    </Typography.Text>
  );
};

const HistoryPopover: FC<
  PropsWithChildren<{
    history: HistoryRecord[];
  }>
> = (props) => {
  if (props.history.length === 0) {
    return props.children;
  }

  const content = (
    <>
      {props.history.map((h, i) => (
        <div key={i}>
          {renderTime(h.time)}
          {h.label ? ` (${h.label})` : ''}
        </div>
      ))}
    </>
  );

  return (
    <Popover trigger={['click', 'hover']} placement="bottom" content={content}>
      <button type="button" aria-label="Show history details">
        {props.children}
      </button>
    </Popover>
  );
};

const renderTime = (time?: number) => {
  if (!time) return 'never';
  return getLocalDisplayFormatFromTimestamp(time, 'sec');
};

export default HistoryDetail;
